import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosWithAuth from '../../utils/axiosWithAuth';
import './PropertyEditModal.scss';

const PropertyEditModal = ({ property, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    area: property?.area || '',
    address: property?.address || '',
    height: property?.height || '',
    roomCount: property?.roomCount || '',
    doorCount: property?.doorCount || '',
    windowCount: property?.windowCount || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Update form data if property changes
    if (property) {
      setFormData({
        area: property.area || '',
        address: property.address || '',
        height: property.height || '',
        roomCount: property.roomCount || '',
        doorCount: property.doorCount || '',
        windowCount: property.windowCount || '',
      });
    }
  }, [property]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (!property?.id) {
        throw new Error('Property ID is required');
      }
      
      const response = await axiosWithAuth().put(`/properties/${property.id}`, formData);
      
      if (response.data) {
        onSave(formData);
      } else {
        throw new Error('Failed to update property');
      }
    } catch (err) {
      console.error('Error updating property:', err);
      setError(err.message || 'Failed to update property');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="property-edit-modal">
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h3>{t('propertyEdit.title')}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {error && (
            <div className="error-message">{error}</div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="area">{t('propertyEdit.area')}</label>
              <input 
                type="number"
                id="area"
                name="area"
                value={formData.area}
                onChange={handleChange}
                placeholder={t('propertyEdit.areaPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="height">{t('propertyEdit.height')}</label>
              <input 
                type="number"
                id="height"
                name="height"
                value={formData.height}
                onChange={handleChange}
                placeholder={t('propertyEdit.heightPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address">{t('propertyEdit.address')}</label>
              <input 
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder={t('propertyEdit.addressPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="roomCount">{t('propertyEdit.roomCount')}</label>
              <input 
                type="number"
                id="roomCount"
                name="roomCount"
                value={formData.roomCount}
                onChange={handleChange}
                placeholder={t('propertyEdit.roomCountPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="doorCount">{t('propertyEdit.doorCount')}</label>
              <input 
                type="number"
                id="doorCount"
                name="doorCount"
                value={formData.doorCount}
                onChange={handleChange}
                placeholder={t('propertyEdit.doorCountPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="windowCount">{t('propertyEdit.windowCount')}</label>
              <input 
                type="number"
                id="windowCount"
                name="windowCount"
                value={formData.windowCount}
                onChange={handleChange}
                placeholder={t('propertyEdit.windowCountPlaceholder')}
              />
            </div>
            
            <div className="button-group">
              <button 
                type="button" 
                className="cancel-button" 
                onClick={onClose}
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button 
                type="submit" 
                className="save-button"
                disabled={loading}
              >
                {loading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PropertyEditModal; 